// controls/inc/OptionsEditor.js
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { BiPlus, BiTrash, BiMoveVertical } from "react-icons/bi";
import { slugify } from "../../utils/stringTools";

/**
 * Éditeur d'options mutualisé (select, radio, checkbox, etc.)
 * avec support du drag & drop pour réordonner
 */
export default function OptionsEditor({
  initialOptions,
  onChange,
  label = "Options",
}) {
  const [options, setOptions] = useState(initialOptions || []);

  // Réinitialise quand on change de champ à éditer
  useEffect(() => {
    setOptions(initialOptions || []);
  }, [initialOptions]);

  // Propagation vers le parent
  useEffect(() => {
    onChange?.(options);
  }, [options]);

  // 🧱 mise à jour d'une option
  const updateOption = (index, key, value) => {
    setOptions((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };

      if (key === "text") {
        const oldSlug = slugify(current.text || "");
        const newSlug = slugify(value || "");
        if (!current.value || current.value === oldSlug) {
          current.value = newSlug;
        }
      }

      current[key] = value;
      updated[index] = current;
      return updated;
    });
  };

  // ➕ Ajout
  const addOption = () =>
    setOptions((prev) => [...prev, { text: "Nouvelle option", value: "" }]);

  // ❌ Suppression
  const removeOption = (index) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  // ↕️ Réordonner via DnD
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(options);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setOptions(reordered);
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-bold d-flex align-items-center gap-2">
        {label}
      </label>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="options-list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {options.map((opt, index) => (
                <Draggable key={index} draggableId={`opt-${index}`} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className={`d-flex align-items-center mb-2 py-1 rounded ${snapshot.isDragging ? "bg-light border" : ""
                        }`}
                    >
                      {/* Drag handle */}
                      <span
                        {...provided.dragHandleProps}
                        className="text-muted me-0"
                        style={{ cursor: "grab" }}
                      >
                        <BiMoveVertical size={18} />
                      </span>

                      {/* Inputs */}
                      <input
                        type="text"
                        className="form-control form-control-sm me-2"
                        placeholder="Texte visible"
                        value={opt.text}
                        onChange={(e) => updateOption(index, "text", e.target.value)}
                      />
                      <input
                        type="text"
                        className="form-control form-control-sm me-2"
                        placeholder="Valeur (slug)"
                        value={opt.value}
                        onChange={(e) => updateOption(index, "value", e.target.value)}
                      />

                      {/* Supprimer */}
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => removeOption(index)}
                      >
                        <BiTrash size={16} />
                      </button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {/* Bouton d’ajout */}
      <button
        type="button"
        className="btn btn-sm btn-outline-primary mt-2"
        onClick={addOption}
      >
        <BiPlus className="me-1" /> Ajouter une option
      </button>
    </div>
  );
}
